import { QuestionType } from '@domain/constant/questionType';
import { PooledQuestionPolicesRs } from '@domain/rs/pass/interview/policies/PooledQuestionPolicesRs';
import { PooledAccQuestionDto } from '@domain/rs/pass/interview/question/PooledAccQuestionListRs';
import { CompanyQuestionVO } from '@domain/vo/pass/interview/question/CompanyQuestionVO';
import { FrequencyQuestionVO } from '@domain/vo/pass/interview/question/FrequencyQuestionVO';
import { MyselfQuestionVO } from '@domain/vo/pass/interview/question/MyselfQuestionVO';
import { useUserInfo } from '@repository/auth/useUserInfo';
import { usePolicies } from '@repository/interviewPractices/pooledQuestions/usePooledQuestions';
import useCheckAuthority from '@utils/hooks/useCheckAuthority';
import React, { useContext, useState } from 'react';

export type InterviewQuestionType = CompanyQuestionVO | FrequencyQuestionVO | MyselfQuestionVO | PooledAccQuestionDto;

interface SelectedAccQuestion {
  accCode: string;
  questionSn: number[];
}
interface InterviewContextType {
  activeTab: QuestionType;
  setActiveTab: (tab: QuestionType) => void;
  selectedQuestionList: InterviewQuestionType[];
  selectAccQuestion: (accCode: string, questionSn: number) => void;
  selectedAccQuestion: SelectedAccQuestion[];
  updateSelectedQuestionList: (select: InterviewQuestionType, duplicate?: boolean) => void;
  policies: PooledQuestionPolicesRs;
  swapQuestionOrder: (targetId: number, nextIdx: number) => void;
}

const InterviewContext = React.createContext<InterviewContextType | null>(null);

export const useInterview = () => {
  const context = useContext(InterviewContext);
  if (!context) {
    throw new Error('useInterview must be used within a InterviewProvider');
  }

  return context;
};

interface InterviewProviderProps {
  children: React.ReactNode;
}

export const InterviewProvider = ({ children }: InterviewProviderProps) => {
  const [activeTab, setActiveTab] = useState<QuestionType>(QuestionType.COMPANY);
  const [selectedQuestionList, setSelectedQuestionList] = useState<InterviewQuestionType[]>([]);
  const [selectedAccQuestion, setSelectedAccQuestion] = useState<SelectedAccQuestion[]>([]);
  const { data: userInfo } = useUserInfo();
  const { isAuthorityUser } = useCheckAuthority(userInfo);
  const { data } = usePolicies({ enabled: isAuthorityUser });
  const {
    interviewPracticeMaxQuestionSize = 10,
    interviewPracticeMinQuestionSize = 1,
    maxLengthQuestionText = 150,
  } = data ?? {};

  const policies = {
    interviewPracticeMaxQuestionSize,
    interviewPracticeMinQuestionSize,
    maxLengthQuestionText,
  };

  const updateSelectedQuestionList = (select: InterviewQuestionType, duplicate = false) => {
    const index = selectedQuestionList.findIndex((item) => item.sn === select.sn);

    if (duplicate) {
      setSelectedQuestionList((prev) => [...prev, select]);
      return;
    }

    if (index > -1) {
      setSelectedQuestionList((prev) => {
        const newList = [...prev];
        newList.splice(index, 1);
        return newList;
      });
    } else {
      setSelectedQuestionList((prev) => [...prev, select]);
    }
  };

  const selectAccQuestion = (accCode: string, questionSn: number) => {
    const index = selectedAccQuestion.findIndex((item) => item.accCode === accCode);

    if (index > -1) {
      const isExist = selectedAccQuestion[index].questionSn.some((v) => v === questionSn);
      if (isExist) {
        setSelectedAccQuestion((prev) => {
          const newList = [...prev];
          newList[index].questionSn = newList[index].questionSn.filter((v) => v !== questionSn);
          return newList;
        });
      } else {
        setSelectedAccQuestion((prev) => {
          const newList = [...prev];
          newList[index].questionSn = [...newList[index].questionSn, questionSn];
          return newList;
        });
      }
    } else {
      setSelectedAccQuestion((prev) => [...prev, { accCode, questionSn: [questionSn] }]);
    }
  };

  const swapQuestionOrder = (targetId: number, nextIdx: number) => {
    if (selectedQuestionList.some((v) => v.sn === targetId)) {
      const currentIdx = selectedQuestionList.findIndex((v) => v.sn === targetId);
      if (currentIdx < nextIdx) {
        // 앞에서 뒤로 옮길 때
        const newSelectedQuestionList = [
          ...selectedQuestionList.slice(0, currentIdx),
          ...selectedQuestionList.slice(currentIdx + 1, nextIdx + 1),
          selectedQuestionList[currentIdx],
          ...selectedQuestionList.slice(nextIdx + 1),
        ];
        setSelectedQuestionList(newSelectedQuestionList);
      } else {
        // 뒤에서 앞으로 옮길 때
        const newSelectedQuestionList = [
          ...selectedQuestionList.slice(0, nextIdx),
          selectedQuestionList[currentIdx],
          ...selectedQuestionList.slice(nextIdx, currentIdx),
          ...selectedQuestionList.slice(currentIdx + 1),
        ];
        setSelectedQuestionList(newSelectedQuestionList);
      }
    }
  };

  return (
    <InterviewContext.Provider
      value={{
        activeTab,
        setActiveTab,
        selectedQuestionList,
        updateSelectedQuestionList,
        policies,
        swapQuestionOrder,
        selectAccQuestion,
        selectedAccQuestion,
      }}
    >
      {children}
    </InterviewContext.Provider>
  );
};
